// Apple Music deep-link helpers.
//
// Single source of truth for building "Open in Apple Music" URLs from
// scanned cards. Uses the user's chosen storefront (ISO 3166-1 alpha-2
// lowercase) when available, falling back to "us".
//
// Deep-link priority (highest first):
//   1) card.apple_music_track_url  (from iTunes enrichment or Songstats)
//   2) https://music.apple.com/{storefront}/song/{apple_music_track_id}
//   3) card.itunes_view_url        (legacy pre-AM1 shape; same URL family)
//   4) card.buy_links.itunes       (search URL from enrichment)
//   5) Constructed Apple Music search URL from title + artist
//
// Any consumer that opens Apple Music should call `getAppleMusicTrackUrl`
// or `getAppleMusicArtistUrl` so the region logic stays in one place.

export const DEFAULT_STOREFRONT = 'us';

// The subset of Apple Music storefronts the settings UI exposes.
// Keeping this narrow avoids overwhelming users; add more here + on
// the backend allowlist to expand.
export const APPLE_MUSIC_STOREFRONTS = [
  { code: 'us', label: 'United States' },
  { code: 'gb', label: 'United Kingdom' },
  { code: 'ie', label: 'Ireland' },
  { code: 'de', label: 'Germany' },
  { code: 'fr', label: 'France' },
  { code: 'nl', label: 'Netherlands' },
  { code: 'es', label: 'Spain' },
  { code: 'it', label: 'Italy' },
  { code: 'se', label: 'Sweden' },
  { code: 'no', label: 'Norway' },
  { code: 'dk', label: 'Denmark' },
  { code: 'fi', label: 'Finland' },
  { code: 'au', label: 'Australia' },
  { code: 'nz', label: 'New Zealand' },
  { code: 'ca', label: 'Canada' },
  { code: 'br', label: 'Brazil' },
  { code: 'mx', label: 'Mexico' },
  { code: 'jp', label: 'Japan' },
  { code: 'kr', label: 'South Korea' },
  { code: 'in', label: 'India' },
  { code: 'za', label: 'South Africa' },
];

const ALLOWED_CODES = new Set(APPLE_MUSIC_STOREFRONTS.map((s) => s.code));

/** Normalize a storefront code, falling back to 'us' for anything unknown. */
export function normalizeStorefront(code) {
  const lowered = (code || '').trim().toLowerCase();
  if (ALLOWED_CODES.has(lowered)) return lowered;
  return DEFAULT_STOREFRONT;
}

// Language / country → Apple Music storefront mapping.
// - When navigator.language is like "en-GB" we prefer the country half.
// - When only a language is present ("de"), we fall back to a reasonable
//   country default (German → 'de', Portuguese → 'br', etc.).
// Codes here MUST all exist in `APPLE_MUSIC_STOREFRONTS`.
const LANGUAGE_ONLY_FALLBACKS = {
  en: 'us',
  de: 'de',
  fr: 'fr',
  nl: 'nl',
  es: 'es',
  it: 'it',
  sv: 'se',
  no: 'no',
  nb: 'no',
  da: 'dk',
  fi: 'fi',
  pt: 'br',
  ja: 'jp',
  ko: 'kr',
  hi: 'in',
  af: 'za',
};

/**
 * Detect an Apple Music storefront from the browser locale (or a passed-in string).
 * Never throws; falls back to 'us' when nothing matches.
 */
export function detectStorefrontFromLocale(localeOverride) {
  let raw = localeOverride;
  if (!raw && typeof navigator !== 'undefined') {
    raw = navigator.language || (navigator.languages && navigator.languages[0]) || '';
  }
  raw = (raw || '').trim().toLowerCase();
  if (!raw) return DEFAULT_STOREFRONT;
  // Try country segment first ("en-gb" → "gb")
  const parts = raw.split(/[-_]/).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const seg = parts[i];
    if (seg.length === 2 && ALLOWED_CODES.has(seg)) return seg;
  }
  // Language-only fallback ("de" → "de")
  const lang = parts[0];
  if (LANGUAGE_ONLY_FALLBACKS[lang]) return LANGUAGE_ONLY_FALLBACKS[lang];
  return DEFAULT_STOREFRONT;
}

/**
 * Resolve the effective storefront for URL building.
 *
 * Preference order:
 *   1. Explicit user-picked storefront (persisted on the user record)
 *   2. Browser-locale-derived storefront (auto-detect)
 *   3. 'us'
 *
 * This is the single choke point every deep-link builder goes through
 * so region behaviour stays consistent whether the user has ever
 * touched the region toggle or not.
 */
export function resolveStorefront(userStorefront, localeOverride) {
  const explicit = (userStorefront || '').trim().toLowerCase();
  if (explicit && ALLOWED_CODES.has(explicit)) return explicit;
  return detectStorefrontFromLocale(localeOverride);
}

/**
 * Some stored Apple Music URLs contain a hard-coded storefront
 * (e.g. "https://music.apple.com/us/song/…"). When the user's
 * storefront preference differs, rewrite the path segment so the link
 * opens in their region. Safe no-op for search URLs and non-music.apple.com hosts.
 */
export function retargetStorefront(url, storefront) {
  if (!url) return url;
  const region = normalizeStorefront(storefront);
  try {
    const u = new URL(url);
    if (u.hostname !== 'music.apple.com' && u.hostname !== 'itunes.apple.com') {
      return url;
    }
    const parts = u.pathname.split('/').filter(Boolean);
    if (!parts.length) return url;
    // The first path segment is the storefront on all music.apple.com deep links.
    // We only replace when it looks like a 2-letter code so we never touch
    // unusual URL shapes (e.g. "/search?term=...").
    if (parts[0].length === 2) {
      parts[0] = region;
      u.pathname = '/' + parts.join('/');
      return u.toString();
    }
    return url;
  } catch (_e) {
    return url;
  }
}

/** Best-effort deep-link URL for a track card (never null). */
export function getAppleMusicTrackUrl(card, storefront) {
  if (!card) return null;
  const region = resolveStorefront(storefront);
  const stored = card.apple_music_track_url || card.itunes_view_url;
  if (stored) return retargetStorefront(stored, region);
  if (card.apple_music_track_id) {
    return `https://music.apple.com/${region}/song/${encodeURIComponent(card.apple_music_track_id)}`;
  }
  if (card.buy_links?.itunes) {
    return retargetStorefront(card.buy_links.itunes, region);
  }
  const q = encodeURIComponent(`${card.artist || ''} ${card.title || ''}`.trim());
  return `https://music.apple.com/${region}/search?term=${q}`;
}

/** Best-effort deep-link URL for an artist page. */
export function getAppleMusicArtistUrl(card, storefront) {
  if (!card) return null;
  const region = resolveStorefront(storefront);
  const stored = card.apple_music_artist_url;
  if (stored) return retargetStorefront(stored, region);
  if (card.apple_music_artist_id) {
    return `https://music.apple.com/${region}/artist/${encodeURIComponent(card.apple_music_artist_id)}`;
  }
  const q = encodeURIComponent(card.artist || '');
  return `https://music.apple.com/${region}/search?term=${q}`;
}

/** True when we have a direct track deep-link (not just a search fallback). */
export function hasAppleMusicDirectLink(card) {
  if (!card) return false;
  return !!(card.apple_music_track_url || card.apple_music_track_id || card.itunes_view_url);
}
